import { useCallback, useEffect, useRef, useState } from 'react';

const STORAGE_KEY = 'recur:pdfExportMs';
const DEFAULT_MS = 5000;
const MIN_MS = 2000;
const MAX_MS = 15000;
const MAX_SAMPLES = 8;
// Cold-start nudge: the first export of a session pays font/canvas warmup the
// rolling average (taken from prior warm exports) doesn't capture.
const COLD_START_FACTOR = 1.25;
// Settle hold so the user sees the bar hit 100% before it clears.
const SETTLE_HOLD_MS = 400;

function readSamples(): number[] {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        return parsed.filter((n) => typeof n === 'number' && Number.isFinite(n) && n > 0);
    } catch {
        return [];
    }
}

function recordSample(ms: number): void {
    try {
        const next = [...readSamples(), ms].slice(-MAX_SAMPLES);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
        // localStorage unavailable (private mode / quota) — estimate stays on default.
    }
}

function clampMs(ms: number): number {
    return Math.min(MAX_MS, Math.max(MIN_MS, ms));
}

function predictMs(isColdStart: boolean): number {
    const samples = readSamples();
    const base = samples.length ? samples.reduce((a, b) => a + b, 0) / samples.length : DEFAULT_MS;
    return clampMs(isColdStart ? base * COLD_START_FACTOR : base);
}

// Decelerating curve approaching ~95% — reads ~80% near predictedMs, then keeps
// creeping. Never sticks at a hard integer; the real settle snaps it to 100.
function curvePercent(elapsed: number, predictedMs: number): number {
    return 95 * (1 - Math.exp((-1.5 * elapsed) / predictedMs));
}

export interface ExportProgressState {
    percent: number;
    secondsLeft: number | null;
    isRunning: boolean;
    track: <T>(run: () => Promise<T>) => Promise<T>;
}

export function useExportProgress(): ExportProgressState {
    const [percent, setPercent] = useState(0);
    const [secondsLeft, setSecondsLeft] = useState<number | null>(null);
    const [isRunning, setIsRunning] = useState(false);

    const rafRef = useRef<number | null>(null);
    const settleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const hasRunThisSessionRef = useRef(false);
    const mountedRef = useRef(true);

    const cancelRaf = useCallback(() => {
        if (rafRef.current != null) {
            cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
        }
    }, []);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            cancelRaf();
            if (settleTimerRef.current != null) clearTimeout(settleTimerRef.current);
        };
    }, [cancelRaf]);

    const track = useCallback(
        async <T>(run: () => Promise<T>): Promise<T> => {
            if (settleTimerRef.current != null) {
                clearTimeout(settleTimerRef.current);
                settleTimerRef.current = null;
            }
            cancelRaf();

            const isColdStart = !hasRunThisSessionRef.current;
            const predictedMs = predictMs(isColdStart);
            const startedAt = Date.now();

            setIsRunning(true);
            setPercent(0);
            setSecondsLeft(Math.max(0, Math.ceil(predictedMs / 1000)));

            const tick = () => {
                if (!mountedRef.current) return;
                const elapsed = Date.now() - startedAt;
                setPercent(curvePercent(elapsed, predictedMs));
                setSecondsLeft(elapsed >= predictedMs ? 0 : Math.max(0, Math.ceil((predictedMs - elapsed) / 1000)));
                rafRef.current = requestAnimationFrame(tick);
            };
            rafRef.current = requestAnimationFrame(tick);

            try {
                const result = await run();
                hasRunThisSessionRef.current = true;
                recordSample(Date.now() - startedAt);
                cancelRaf();
                if (mountedRef.current) {
                    setPercent(100);
                    setSecondsLeft(0);
                    settleTimerRef.current = setTimeout(() => {
                        if (!mountedRef.current) return;
                        setIsRunning(false);
                        setPercent(0);
                        setSecondsLeft(null);
                        settleTimerRef.current = null;
                    }, SETTLE_HOLD_MS);
                }
                return result;
            } catch (err) {
                hasRunThisSessionRef.current = true;
                cancelRaf();
                if (mountedRef.current) {
                    setIsRunning(false);
                    setPercent(0);
                    setSecondsLeft(null);
                }
                throw err;
            }
        },
        [cancelRaf],
    );

    return { percent, secondsLeft, isRunning, track };
}
